import { useState, useEffect, useMemo } from "react";
import { useSelector } from "react-redux";
import { Box, Grid, CircularProgress } from "@mui/material";
import {
  DialogComponent,
  SearchBarComponent,
} from "../../../atom";
import DocumentBreadcrumbs from "../../Document/BreadCrumb";
import QuizMarkCard from "../../Cards/QuizMarkCard";
import GuestRepository from "../../../../core/repository/QuizeRepository";
import { getThumbnailSrc, normalizeQuizDirectoryData } from "../../../../core/Constant";

const IS_DIRECTORY_FOLDER = 1;
const ROOT_BREADCRUMB = { name: "Quizzes" };

const isFolder = (item) => Number(item?.is_directory) === IS_DIRECTORY_FOLDER;

const SelectQuizModal = ({
  open,
  onClose,
  courseID,
  onSelect,
  initialFolderStack,
  selectedQuizId,
}) => {
  const mainDetailsState = useSelector((state) => state.course.get("mainDetailsState"));
  const courseName = mainDetailsState?.name || "";

  const [quizzesAndFolders, setQuizzesAndFolders] = useState([]);
  const [currentFolderId, setCurrentFolderId] = useState(null);
  const [folderStack, setFolderStack] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchQuizzes = async (parentId = null, search = searchQuery) => {
    if (!courseID) return;
    setLoading(true);
    try {
      const payload = {
        course_id: Number(courseID),
        parent_id: parentId,
        search: parentId !== null ? "" : search || "",
      };
      const response = await GuestRepository.onGetAllQuizList(payload);
      const apiData = response?.data || response;
      const activeQuizDirectories = (apiData?.quiz_directories || []).filter(
        (item) => (!item?.quiz_name && !item?.quiz_id) || Number(item?.status) === 1
      );
      const activeQuizzes = (apiData?.quizzes || apiData?.quiz || []).filter(
        (item) => Number(item?.status) === 1
      );
      const sortedData = normalizeQuizDirectoryData(
        activeQuizDirectories,
        activeQuizzes
      );
      setQuizzesAndFolders(sortedData);
    } catch (error) {
      console.error("Error fetching quizzes:", error);
      setQuizzesAndFolders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open && courseID) {
      if (initialFolderStack && initialFolderStack.length > 0) {
        const lastFolder = initialFolderStack[initialFolderStack.length - 1];
        const lastFolderId = lastFolder?.id ?? null;
        setCurrentFolderId(lastFolderId);
        setFolderStack(initialFolderStack);
        fetchQuizzes(lastFolderId, "");
      } else {
        setCurrentFolderId(null);
        setFolderStack([]);
        fetchQuizzes(null, "");
      }
      setSearchQuery("");
    }
  }, [open, courseID, initialFolderStack]);

  const displayList = useMemo(() => {
    let items = quizzesAndFolders;
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      items = items.filter((d) => d.name?.toLowerCase().includes(q));
    }
    return items;
  }, [quizzesAndFolders, searchQuery]);

  const openFolder = (folder) => {
    if (loading) return;
    setCurrentFolderId(folder.id);
    setFolderStack((prev) => {
      if (prev.find((f) => f.id === folder.id)) return prev;
      return [...prev, { id: folder.id, name: folder.name }];
    });
    fetchQuizzes(folder.id, "");
  };

  const navigateToBreadcrumb = (index) => {
    if (index === 0 || (courseName && index === 1)) {
      setCurrentFolderId(null);
      setFolderStack([]);
      fetchQuizzes(null, "");
      return;
    }
    const folderIndex = index - (courseName ? 2 : 1);
    const newStack = folderStack.slice(0, folderIndex + 1);
    setFolderStack(newStack);
    const folderId = newStack[folderIndex]?.id ?? null;
    setCurrentFolderId(folderId);
    fetchQuizzes(folderId, "");
  };

  const breadcrumbTrail = useMemo(() => {
    const trail = [ROOT_BREADCRUMB];
    if (courseName) {
      trail.push({ id: `course-${courseID}`, name: courseName });
    }
    return [...trail, ...folderStack];
  }, [courseName, courseID, folderStack]);

  const searchInput = (value) => {
    setSearchQuery(value);
    fetchQuizzes(currentFolderId, value);
  };

  const handleCardAction = (item) => {
    if (isFolder(item)) {
      openFolder(item);
      return;
    }
    onSelect(item, currentFolderId, folderStack);
  };

  return (
    <DialogComponent
      title="Select Quiz"
      open={open}
      maxwidth="lg"
      onClose={onClose}
      isShowCloseButton={true}
    >
      <Box p={3} sx={{ position: "relative", minHeight: "450px" }}>
        <Grid container justifyContent="space-between" alignItems="center" mb={2}>
          <Grid item>
            <DocumentBreadcrumbs
              breadcrumbTrail={breadcrumbTrail}
              onBreadcrumbClick={navigateToBreadcrumb}
            />
          </Grid>
          <Grid item>
            <SearchBarComponent
              value={searchQuery}
              onchange={(e) => searchInput(e.target.value)}
            />
          </Grid>
        </Grid>

        {loading && (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              bgcolor: "rgba(255,255,255,0.6)",
              zIndex: 9,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            <CircularProgress sx={{ color: "#9834F0" }} />
          </Box>
        )}

        <Box sx={{ mt: 2 }}>
          {displayList.length === 0 ? (
            <span className="no-review-text">
              Currently, there are no active Quizzes available in this folder.
            </span>
          ) : (
            <Grid container rowGap={2} columnGap={4}>
              {displayList.map((item, index) => {
                const folder = isFolder(item);
                const isSelected = !folder && selectedQuizId && (String(item.id) === String(selectedQuizId));
                const folderImage = getThumbnailSrc(item?.thumbnail, "folder_thumbnail");

                return (
                  <Grid key={item.id || index} item>
                    <QuizMarkCard
                      isFolder={folder}
                      title={item.name}
                      duration={item.duration}
                      isSelectionMode={true}
                      selected={isSelected}
                      onSelect={() => handleCardAction(item)}
                      onViewFolder={() => handleCardAction(item)}
                      thumbnail={folderImage}
                    />
                  </Grid>
                );
              })}
            </Grid>
          )}
        </Box>
      </Box>
    </DialogComponent>
  );
};

export default SelectQuizModal;
